import { DOCUMENT } from '@angular/common';
import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
  Signal,
} from '@angular/core';

export type ThemeMode = 'light' | 'dark';

/**
 * localStorage key holding the user's explicit choice.
 *
 * ⚠ Duplicated verbatim in `src/index.html`, which reads it in an inline
 * script before first paint and therefore cannot import this constant.
 * Changing it here means changing it there.
 */
const STORAGE_KEY = 'mojito-bi.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Substring identifying the dark ng-zorro stylesheet emitted by the
 * `theme-dark` bundle (see `angular.json`). The filename is content-hashed,
 * so the bundle name is all we can match on.
 */
const DARK_BUNDLE_HREF = 'theme-dark';

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

/**
 * Owns the app-wide light/dark theme.
 *
 * Two things have to move together, and this service is the only place that
 * knows both:
 *
 * 1. `<html data-theme>` — drives the CSS custom properties emitted by
 *    `styles/base/theme-vars.less`, i.e. every application component style.
 * 2. The `media` attribute of the `theme-dark` stylesheet — drives the
 *    ng-zorro layer. `media="all"` activates it, `media="not all"` makes it
 *    inert. The light bundle is never touched: it stays active in both themes
 *    and `theme-dark` overrides it by source order.
 *
 * The same two steps run in the inline boot script in `src/index.html` so the
 * first paint is already correct; this service takes over from there.
 *
 * Provided in `app.config.ts` (following the `GlobalLoadingService`
 * precedent) rather than `providedIn: 'root'`, which the repo bans. It cannot
 * be route-scoped: `auth/callback` and `select-company` live outside the main
 * layout and still need a theme.
 */
@Injectable()
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly mediaQuery: MediaQueryList | null =
    this.document.defaultView?.matchMedia(DARK_QUERY) ?? null;

  /** `null` until the user picks a theme explicitly; then it wins forever. */
  private readonly _explicit = signal<ThemeMode | null>(this.readStoredMode());
  private readonly _systemDark = signal<boolean>(this.mediaQuery?.matches ?? false);

  readonly theme: Signal<ThemeMode> = computed(() =>
    this._explicit() ?? (this._systemDark() ? 'dark' : 'light'),
  );

  readonly isDark: Signal<boolean> = computed(() => this.theme() === 'dark');

  constructor() {
    this.watchSystemPreference();

    effect(() => {
      this.applyTheme(this.theme());
    });
  }

  set(mode: ThemeMode): void {
    this._explicit.set(mode);
    try {
      this.document.defaultView?.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Storage disabled (private mode). The signal still drives the session.
    }
  }

  toggle(): void {
    this.set(this.isDark() ? 'light' : 'dark');
  }

  /**
   * Only matters while the user has made no explicit choice — after that
   * `_explicit` shadows it — but keeping the listener alive costs nothing and
   * avoids a stale value if the choice is ever cleared.
   */
  private watchSystemPreference(): void {
    const mediaQuery = this.mediaQuery;
    if (!mediaQuery) {
      return;
    }

    const onChange = (event: MediaQueryListEvent): void =>
      this._systemDark.set(event.matches);

    mediaQuery.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() =>
      mediaQuery.removeEventListener('change', onChange),
    );
  }

  private applyTheme(mode: ThemeMode): void {
    // setAttribute rather than dataset['theme'] — the latter trips
    // `noUncheckedIndexedAccess`.
    this.document.documentElement.setAttribute('data-theme', mode);

    const media = mode === 'dark' ? 'all' : 'not all';
    this.document
      .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
      .forEach((link) => {
        if (!(link.getAttribute('href') ?? '').includes(DARK_BUNDLE_HREF)) {
          return;
        }
        // Mirrors the boot script: Angular's `styles.inlineCritical` rewrites
        // stylesheet links to `media="print" onload="this.media='all'"`, and
        // that handler would undo the line below when the sheet finishes
        // downloading. The optimisation is off in angular.json; this keeps us
        // correct if it is ever turned back on.
        link.removeAttribute('onload');
        link.media = media;
      });
  }

  private readStoredMode(): ThemeMode | null {
    try {
      const stored = this.document.defaultView?.localStorage.getItem(STORAGE_KEY) ?? null;
      return isThemeMode(stored) ? stored : null;
    } catch {
      return null;
    }
  }
}
